/**
 * User Detail - Admin view of a user with tabs and actions
 */
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { MIN_PASSWORD_LENGTH } from "@jourfx/convex/lib/password";
import {
  ArrowLeft,
  User,
  Mail,
  Building2,
  Shield,
  Calendar,
  CheckCircle,
  XCircle,
  Monitor,
  Activity,
  MoreVertical,
  Loader2,
  ShieldAlert,
  LogOut,
  UserX,
  Key,
  Users,
  Power,
  AlertTriangle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  useToast,
  AvatarUpload,
  Card,
  CardContent,
  Skeleton,
} from "@jourfx/ui";
import { useSession } from "../../lib/auth";

// Tab types
type TabId = "profile" | "sessions" | "activity";

const PASSWORD_ROLES = new Set(["gutachter", "assistent", "administrator"]);

function getUserRoleLabel(
  t: (key: string) => string,
  role: string
): string {
  switch (role) {
    case "administrator":
      return t("roles.administrator");
    case "gutachter":
      return t("roles.gutachter");
    case "assistent":
      return t("roles.assistent");
    case "versicherter":
      return t("roles.versicherter");
    case "super_admin":
      return t("roles.super_admin");
    default:
      return role;
  }
}

function getStatusLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  status: string
): string {
  switch (status) {
    case "active":
      return t("status.active");
    case "inactive":
      return t("status.inactive");
    case "pending":
      return t("status.pending");
    case "locked":
      return t("status.locked");
    case "disabled":
      return t("status.disabled");
    default:
      return status;
  }
}

// Confirmation dialog state
interface ConfirmDialog {
  open: boolean;
  title: string;
  message: string;
  confirmText: string;
  variant: "danger" | "warning";
  onConfirm: () => void;
}

export function UserDetail() {
  const { t } = useTranslation("users");
  const { t: tc } = useTranslation("common");
  const apiRef = api as any;
  const { userId } = useParams({ strict: false }) as { userId: string };
  const [activeTab, setActiveTab] = useState<TabId>("profile");
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog | null>(null);
  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [setPasswordError, setSetPasswordError] = useState<string | null>(null);
  const [isSettingPassword, setIsSettingPassword] = useState(false);

  const session = useSession();
  const { toast } = useToast();

  // Fetch user data with auth info
  const userData = useQuery(apiRef.admin.authManagement.getAuthUser, {
    userId: userId,
  });

  // Fetch sessions when on sessions tab
  const sessions = useQuery(
    apiRef.admin.authManagement.listUserSessions,
    activeTab === "sessions" ? { userId: userId } : "skip"
  );
  const resetPassword = useMutation(apiRef.admin.users.resetPassword);
  const resendInvitation = useMutation(apiRef.admin.users.resendInvitation);
  const setPasswordMutation = useMutation(apiRef.admin.users.setPassword);
  const updateUserMutation = useMutation(apiRef.admin.users.update);
  const revokeSessionMutation = useMutation(apiRef.admin.authManagement.revokeSession);
  const revokeAllSessionsMutation = useMutation(apiRef.admin.authManagement.revokeAllSessions);
  const disableUserMutation = useMutation(apiRef.admin.authManagement.disableUser);
  const enableUserMutation = useMutation(apiRef.admin.authManagement.enableUser);

  // API call helper for HTTP actions
  const callHttpAction = useCallback(async (path: string, body: object) => {
    const baseUrl = import.meta.env.VITE_CONVEX_SITE_URL;
    const response = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Request failed");
    }
    return response.json();
  }, []);

  const clientUserAgent =
    typeof navigator === "undefined" ? "unknown" : navigator.userAgent;

  // Action handlers
  const handleRevokeSession = useCallback(async (sessionId: string) => {
    setIsLoading(true);
    try {
      await revokeSessionMutation({
        userId: userId as any,
        sessionId,
        userAgent: clientUserAgent,
      });
      toast({
        title: t("actions.revokeSession"),
        description: t("detail.userSessionRevoked", "Session was revoked."),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [clientUserAgent, revokeSessionMutation, t, tc, toast, userId]);

  const handleRevokeAllSessions = useCallback(() => {
    setConfirmDialog({
      open: true,
      title: t("actions.revokeSessions"),
      message: t("actions.revokeSessionsConfirm"),
      confirmText: t("actions.revokeSessionsButton"),
      variant: "warning",
      onConfirm: async () => {
        setIsLoading(true);
        try {
          await revokeAllSessionsMutation({
            userId: userId as any,
            userAgent: clientUserAgent,
          });
          toast({
            title: t("actions.revokeSessions"),
            description: t("detail.userSessionsRevoked", "All sessions were revoked."),
          });
        } catch (error) {
          toast({
            title: tc("error.title"),
            description: error instanceof Error ? error.message : tc("error.unknown"),
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
          setConfirmDialog(null);
        }
      },
    });
    setActionMenuOpen(false);
  }, [clientUserAgent, revokeAllSessionsMutation, t, tc, toast, userId]);

  const handleDisableUser = useCallback(() => {
    setConfirmDialog({
      open: true,
      title: t("actions.disable"),
      message: t("actions.disableConfirm"),
      confirmText: t("actions.disableButton"),
      variant: "danger",
      onConfirm: async () => {
        setIsLoading(true);
        try {
          await disableUserMutation({
            userId: userId as any,
            reason: "Disabled by admin",
            userAgent: clientUserAgent,
          });
          toast({
            title: t("actions.disable"),
            description: t("detail.userDisabled", "User was disabled."),
          });
        } catch (error) {
          toast({
            title: tc("error.title"),
            description: error instanceof Error ? error.message : tc("error.unknown"),
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
          setConfirmDialog(null);
        }
      },
    });
    setActionMenuOpen(false);
  }, [clientUserAgent, disableUserMutation, t, tc, toast, userId]);

  const handleEnableUser = useCallback(() => {
    setConfirmDialog({
      open: true,
      title: t("actions.enable"),
      message: t("actions.enableConfirm"),
      confirmText: t("actions.enableButton"),
      variant: "warning",
      onConfirm: async () => {
        setIsLoading(true);
        try {
          await enableUserMutation({
            userId: userId as any,
            userAgent: clientUserAgent,
          });
          toast({
            title: t("actions.enable"),
            description: t("detail.userEnabled", "User was enabled."),
          });
        } catch (error) {
          toast({
            title: tc("error.title"),
            description: error instanceof Error ? error.message : tc("error.unknown"),
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
          setConfirmDialog(null);
        }
      },
    });
    setActionMenuOpen(false);
  }, [clientUserAgent, enableUserMutation, t, tc, toast, userId]);

  const handleResetPassword = useCallback(() => {
    setConfirmDialog({
      open: true,
      title: t("actions.resetPassword"),
      message: t("actions.resetPasswordConfirm", "Ein Passwort-Reset-Link wird per E-Mail gesendet."),
      confirmText: t("actions.resetPasswordButton", "Reset-Link senden"),
      variant: "warning",
      onConfirm: async () => {
        setIsLoading(true);
        try {
          await resetPassword({ id: userId });
          toast({
            title: t("actions.resetPassword"),
            description: t("actions.resetPasswordSuccess", "Reset-Link wurde gesendet."),
          });
        } catch (error) {
          toast({
            title: tc("error.title"),
            description: error instanceof Error ? error.message : tc("error.unknown"),
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
          setConfirmDialog(null);
        }
      },
    });
    setActionMenuOpen(false);
  }, [t, tc, toast, resetPassword, userId]);

  const handleResendInvitation = useCallback(() => {
    setConfirmDialog({
      open: true,
      title: t("actions.resendInvite"),
      message: t(
        "actions.resendInviteConfirm",
        "Eine neue Einladungs-E-Mail wird an diesen Benutzer gesendet."
      ),
      confirmText: t("actions.resendInviteButton", "Einladung senden"),
      variant: "warning",
      onConfirm: async () => {
        setIsLoading(true);
        try {
          await resendInvitation({ id: userId });
          toast({
            title: t("actions.resendInvite"),
            description: t(
              "actions.resendInviteSuccess",
              "Einladungs-E-Mail wurde gesendet."
            ),
          });
        } catch (error) {
          toast({
            title: tc("error.title"),
            description: error instanceof Error ? error.message : tc("error.unknown"),
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
          setConfirmDialog(null);
        }
      },
    });
    setActionMenuOpen(false);
  }, [resendInvitation, t, tc, toast, userId]);

  const handleOpenSetPassword = useCallback(() => {
    setSetPasswordError(null);
    setNewPassword("");
    setConfirmPassword("");
    setSetPasswordOpen(true);
    setActionMenuOpen(false);
  }, []);

  const handleSetPassword = useCallback(async () => {
    setSetPasswordError(null);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setSetPasswordError(
        t("passwordMinLength", "Password must be at least {{min}} characters.", {
          min: MIN_PASSWORD_LENGTH,
        })
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setSetPasswordError(t("passwordMismatch", "Passwords do not match."));
      return;
    }

    setIsSettingPassword(true);
    try {
      await setPasswordMutation({ id: userId, newPassword });
      toast({
        title: t("actions.setPassword", "Set Password"),
        description: t("actions.setPasswordSuccess", "Password was updated successfully."),
      });
      setSetPasswordOpen(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("actions.setPasswordError", "Failed to set password.");
      setSetPasswordError(message);
      toast({
        title: tc("error.title"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSettingPassword(false);
    }
  }, [
    confirmPassword,
    newPassword,
    setPasswordMutation,
    t,
    tc,
    toast,
    userId,
  ]);

  const handleImpersonate = useCallback(() => {
    setConfirmDialog({
      open: true,
      title: t("actions.impersonate"),
      message: t("actions.impersonateConfirm"),
      confirmText: t("actions.impersonateButton"),
      variant: "warning",
      onConfirm: async () => {
        setIsLoading(true);
        try {
          const result = await callHttpAction("/api/admin/execute-impersonation", {
            targetUserId: userId,
          });
          // On success, redirect to the internal app
          if (result.redirectUrl) {
            window.location.href = result.redirectUrl;
          } else {
            toast({
              title: t("actions.impersonate"),
              description: t("actions.impersonateSuccess"),
            });
          }
        } catch (error) {
          console.error("Failed to impersonate user:", error);
          toast({
            title: tc("error.title"),
            description: error instanceof Error ? error.message : tc("error.unknown"),
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
          setConfirmDialog(null);
        }
      },
    });
    setActionMenuOpen(false);
  }, [userId, t, tc, toast, callHttpAction]);

  // Loading state
  if (userData === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not found
  if (userData === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <ShieldAlert className="h-12 w-12 text-warning" />
        <h2 className="text-xl font-semibold text-foreground">{t("detail.notFound")}</h2>
        <Link
          to="/users"
          className="inline-flex items-center gap-2 text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          {tc("actions.back")}
        </Link>
      </div>
    );
  }

  const { user, auth, sessionCount } = userData;
  const canSetPassword =
    PASSWORD_ROLES.has(user.role) && user.role !== "versicherter";
  const canResendInvitation = Boolean(userData.pendingInvitation);

  const tabs = [
    { id: "profile" as TabId, label: t("detail.tabs.profile"), icon: User },
    { id: "sessions" as TabId, label: t("detail.tabs.sessions"), icon: Monitor },
    { id: "activity" as TabId, label: t("detail.tabs.activity"), icon: Activity },
  ];

  return (
    <div className="space-y-6">
      {/* Confirmation Dialog */}
      {confirmDialog?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-card p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`rounded-full p-2 ${
                confirmDialog.variant === "danger" ? "bg-error-muted" : "bg-warning-muted"
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  confirmDialog.variant === "danger" ? "text-error" : "text-warning"
                }`} />
              </div>
              <h3 className="text-lg font-semibold text-foreground">{confirmDialog.title}</h3>
            </div>
            <p className="text-muted-foreground mb-6">{confirmDialog.message}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="rounded-lg border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50"
                disabled={isLoading}
              >
                {tc("actions.cancel")}
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                disabled={isLoading}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${
                  confirmDialog.variant === "danger"
                    ? "bg-error text-error-foreground hover:bg-error/90"
                    : "bg-warning text-warning-foreground hover:bg-warning/90"
                }`}
              >
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {confirmDialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={setPasswordOpen} onOpenChange={setSetPasswordOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("actions.setPassword", "Set Password")}</DialogTitle>
            <DialogDescription>
              {t(
                "actions.setPasswordConfirm",
                "Set a new password for this user. This will revoke all existing sessions."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">{t("newPassword", "New password")}</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "passwordRequirements",
                  "At least 12 characters and 3 of 4 character classes."
                )}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">
                {t("confirmPassword", "Confirm password")}
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            {setPasswordError && (
              <div className="rounded-md border border-error-muted bg-error-muted/20 px-3 py-2 text-sm text-error">
                {setPasswordError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetPasswordOpen(false)} disabled={isSettingPassword}>
              {tc("actions.cancel")}
            </Button>
            <Button onClick={handleSetPassword} disabled={isSettingPassword}>
              {isSettingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("actions.setPasswordButton", "Set password")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Back link and header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/users"
            className="rounded-full p-2 hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5 text-muted-foreground" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{user.name}</h1>
            <p className="text-muted-foreground">{user.email}</p>
          </div>
        </div>

        {/* Actions dropdown */}
        <div className="relative">
          <button
            onClick={() => setActionMenuOpen(!actionMenuOpen)}
            className="inline-flex items-center gap-2 rounded-lg border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50"
          >
            {tc("table.actions")}
            <MoreVertical className="h-4 w-4" />
          </button>

              {actionMenuOpen && (
                <>
                  {/* Backdrop to close menu */}
                  <button
                    type="button"
                    aria-label={tc("table.actions")}
                    className="fixed inset-0 z-10"
                    onClick={() => setActionMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 rounded-lg border bg-card shadow-lg z-20">
                    <div className="py-1">
                      <button
                        onClick={handleOpenSetPassword}
                        className={`flex w-full items-center gap-2 px-4 py-2 text-sm ${
                      canSetPassword
                        ? "text-foreground hover:bg-muted"
                        : "text-muted-foreground cursor-not-allowed"
                    }`}
                    disabled={!canSetPassword}
                    title={
                      canSetPassword
                        ? undefined
                        : t(
                            "actions.setPasswordDisabled",
                            "Password login is not available for this user."
                          )
                    }
                  >
                    <Key className="h-4 w-4" />
                    {t("actions.setPassword", "Set Password")}
                  </button>
                  <button
                    onClick={handleResetPassword}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                  >
                    <Key className="h-4 w-4" />
                    {t("actions.resetPassword")}
                  </button>
                  {canResendInvitation && (
                    <button
                      onClick={handleResendInvitation}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                    >
                      <Mail className="h-4 w-4" />
                      {t("actions.resendInvite")}
                    </button>
                  )}
                  <button
                    onClick={handleRevokeAllSessions}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                  >
                    <LogOut className="h-4 w-4" />
                    {t("actions.revokeSessions")}
                  </button>
                  {session.isSuperAdmin && (
                    <>
                      <hr className="my-1" />
                      <button
                        onClick={handleImpersonate}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                      >
                        <Users className="h-4 w-4" />
                        {t("actions.impersonate")}
                      </button>
                    </>
                  )}
                  <hr className="my-1" />
                  {user.status === "disabled" ? (
                    <button
                      onClick={handleEnableUser}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-success hover:bg-success-muted"
                    >
                      <Power className="h-4 w-4" />
                      {t("actions.enable")}
                    </button>
                  ) : (
                    <button
                      onClick={handleDisableUser}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-error hover:bg-error-muted"
                    >
                      <UserX className="h-4 w-4" />
                      {t("actions.disable")}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium ${
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:border-input hover:text-foreground"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.id === "sessions" && sessionCount > 0 && (
                <span className="ml-1 rounded-full bg-info-muted px-2 py-0.5 text-xs text-info-muted-foreground">
                  {sessionCount}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="rounded-lg border bg-card p-6">
        {activeTab === "profile" && (
          <ProfileTab
            user={user}
            auth={auth}
            onSave={async (values) => {
              await updateUserMutation({
                id: user._id,
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phone: values.phone,
              });
            }}
          />
        )}
        {activeTab === "sessions" && (
          <SessionsTab
            sessions={sessions}
            onRevokeSession={handleRevokeSession}
            isLoading={isLoading}
          />
        )}
        {activeTab === "activity" && (
          <ActivityTab userId={userId} />
        )}
      </div>
    </div>
  );
}

// Profile Tab Component
function ProfileTab({
  user,
  auth,
  onSave,
}: {
  user: any;
  auth: any;
  onSave: (values: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  }) => Promise<void>;
}) {
  const { t } = useTranslation("users");
  const { t: tc } = useTranslation("common");
  const { toast } = useToast();
  const generateAvatarUploadUrl = useMutation(api.admin.users.generateAvatarUploadUrl);
  const setUserAvatar = useMutation(api.admin.users.setUserAvatar);
  const removeUserAvatar = useMutation(api.admin.users.removeUserAvatar);
  const licensing = useQuery(api.admin.licenseAssignments.getForUser, { userId: user._id });
  const setSeat = useMutation(api.admin.licenseAssignments.setForUser);
  const licenseEntries = licensing?.licenses ?? [];
  const [firstName, setFirstName] = useState(user.firstName ?? "");
  const [lastName, setLastName] = useState(user.lastName ?? "");
  const [email, setEmail] = useState(user.email ?? "");
  const [phone, setPhone] = useState(user.phone ?? "");
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  useEffect(() => {
    setFirstName(user.firstName ?? "");
    setLastName(user.lastName ?? "");
    setEmail(user.email ?? "");
    setPhone(user.phone ?? "");
  }, [user.email, user.firstName, user.lastName, user.phone]);

  const handleSaveProfile = useCallback(async () => {
    setIsSavingProfile(true);
    try {
      await onSave({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim() || undefined,
      });
      toast({
        title: t("detail.profileTitle", "Profile"),
        description: t("detail.profileSaved", "User profile saved."),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsSavingProfile(false);
    }
  }, [email, firstName, lastName, onSave, phone, t, tc, toast]);

  return (
    <div className="space-y-6">
      {/* Avatar */}
      <AvatarUpload
        currentAvatarUrl={user.avatarUrl ?? null}
        userName={user.name || "U"}
        generateUploadUrl={() => generateAvatarUploadUrl()}
        onUploadComplete={async (storageId) => {
          await setUserAvatar({ userId: user._id, storageId: storageId as any });
          toast({ title: t("detail.avatarSaved") });
        }}
        onRemove={async () => {
          await removeUserAvatar({ userId: user._id });
          toast({ title: t("detail.avatarRemoved") });
        }}
        labels={{
          upload: t("detail.avatarUpload"),
          remove: t("detail.avatarRemove"),
          cropTitle: t("detail.avatarCropTitle"),
          cropConfirm: t("detail.avatarCropConfirm"),
          cropCancel: t("detail.avatarCropCancel"),
        }}
      />

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <div className="text-sm font-medium text-foreground">
              {t("detail.profileTitle", "Profile")}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="profile-first-name">{t("detail.firstName", "First name")}</Label>
              <Input
                id="profile-first-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-last-name">{t("detail.lastName", "Last name")}</Label>
              <Input
                id="profile-last-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-email">{t("detail.email")}</Label>
              <Input
                id="profile-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-phone">{t("detail.phone", "Phone")}</Label>
              <Input
                id="profile-phone"
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
              {isSavingProfile && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("detail.saveProfile", "Save profile")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Info Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <InfoItem
          icon={Mail}
          label={t("detail.email")}
          value={user.email}
          badge={
            auth?.emailVerified ? (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-success-muted px-2 py-0.5 text-xs text-success-muted-foreground">
                <CheckCircle className="h-3 w-3" />
                {t("verified")}
              </span>
            ) : (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                <XCircle className="h-3 w-3" />
                {t("unverified")}
              </span>
            )
          }
        />
        <InfoItem
          icon={Building2}
          label={t("detail.organization")}
          value={user.organizationName ?? t("detail.organizationUnavailable", "Unavailable")}
        />
        <InfoItem
          icon={Shield}
          label={t("detail.role")}
          value={
            <span className="inline-flex rounded-full bg-info-muted px-2 py-1 text-xs font-medium text-info-muted-foreground">
              {getUserRoleLabel(t, user.role)}
            </span>
          }
        />
        <InfoItem
          icon={Activity}
          label={t("detail.status")}
          value={
            <span
              className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                user.status === "active"
                  ? "bg-success-muted text-success-muted-foreground"
                  : user.status === "pending"
                  ? "bg-warning-muted text-warning-muted-foreground"
                  : user.status === "locked"
                  ? "bg-error-muted text-error-muted-foreground"
                  : user.status === "disabled"
                  ? "bg-muted text-muted-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {getStatusLabel(tc, user.status)}
            </span>
          }
        />
        <InfoItem
          icon={Calendar}
          label={t("detail.createdAt")}
          value={new Date(user.createdAt).toLocaleDateString()}
        />
      </div>

      {/* Licensing (v2) */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <div className="text-sm font-medium text-foreground">
              {t("detail.licensingTitle", "Licensing")}
            </div>
            <div className="text-sm text-muted-foreground">
              {t("detail.licensingSubtitle", "Assign a base seat (login) and optional module seats for this user.")}
            </div>
          </div>

          {licensing === undefined && (
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {licensing !== undefined && licenseEntries.length === 0 && (
            <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-foreground">
              {t(
                "detail.noV2Licenses",
                "No active v2 licenses are loaded for this installation. Load a signed license token in Licenses."
              )}
            </div>
          )}

          {licensing !== undefined && licenseEntries.length > 0 && (
            <div className="space-y-2">
              {licensing.user?.role !== "versicherter" && !licensing.hasBaseSeat && (
                <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-foreground">
                  {t("detail.noBaseSeat", "This user has no base seat and cannot log in.")}
                </div>
              )}

              {licenseEntries.map((l: any) => (
                <div
                  key={l._id}
                  className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">
                      {l.kind === "base"
                        ? t("detail.baseLicense", "Base License")
                        : t("detail.moduleLicense", "Module License") + (l.moduleId ? ` (${l.moduleId})` : "")}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("detail.seats", "Seats")}: {l.assignedCount}/{l.seats ?? "?"}
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!l.isAssignedToUser}
                      onChange={async (e) => {
                        const assigned = e.target.checked;
                        try {
                          await setSeat({ userId: user._id, licenseId: l._id, assigned });
                          toast({
                            title: assigned ? t("detail.seatAssigned", "Seat assigned") : t("detail.seatRemoved", "Seat removed"),
                          });
                        } catch (error) {
                          toast({
                            title: tc("error.title"),
                            description: error instanceof Error ? error.message : tc("error.unknown"),
                            variant: "destructive",
                          });
                        }
                      }}
                    />
                    <span>{t("detail.assigned", "Assigned")}</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Info Item Component
function InfoItem({
  icon: Icon,
  label,
  value,
  badge,
}: {
  icon: any;
  label: string;
  value: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="rounded-lg bg-muted p-2">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <div className="flex items-center">
          <p className="font-medium text-foreground">{value}</p>
          {badge}
        </div>
      </div>
    </div>
  );
}

// Sessions Tab Component
function SessionsTab({
  sessions,
  onRevokeSession,
  isLoading,
}: {
  sessions: any[] | undefined;
  onRevokeSession: (sessionId: string) => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation("users");

  if (sessions === undefined) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Monitor className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
        {t("detail.noSessions")}
      </div>
    );
  }

  // Parse user agent to extract browser/device info
  const parseUserAgent = (ua: string) => {
    if (!ua || ua === "Unknown") return "Unknown device";

    // Simple parsing - could be enhanced with a library
    if (ua.includes("Chrome")) return "Chrome Browser";
    if (ua.includes("Firefox")) return "Firefox Browser";
    if (ua.includes("Safari")) return "Safari Browser";
    if (ua.includes("Edge")) return "Edge Browser";
    if (ua.includes("Mobile")) return "Mobile Device";

    return ua.substring(0, 50) + (ua.length > 50 ? "..." : "");
  };

  return (
    <div className="space-y-4">
      {sessions.map((session) => (
        <div
          key={session.id}
          className="flex items-center justify-between rounded-lg border p-4"
        >
          <div className="flex items-center gap-4">
            <Monitor className="h-8 w-8 text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">
                {parseUserAgent(session.userAgent)}
              </p>
              <p className="text-sm text-muted-foreground">
                IP: {session.ipAddress || "Unknown"} | {t("detail.sessionCreated")}:{" "}
                {new Date(session.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {session.isActive ? (
              <span className="rounded-full bg-success-muted px-2 py-1 text-xs text-success-muted-foreground">
                {t("detail.sessionActive")}
              </span>
            ) : (
              <span className="rounded-full bg-muted px-2 py-1 text-xs text-muted-foreground">
                {t("detail.sessionExpired")}
              </span>
            )}
            {session.isActive && (
              <button
                onClick={() => onRevokeSession(session.id)}
                disabled={isLoading}
                className="rounded p-2 text-muted-foreground hover:bg-muted hover:text-error disabled:opacity-50"
                title={t("actions.revokeSession")}
              >
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Activity Tab Component
function ActivityTab({ userId }: { userId: string }) {
  const { t } = useTranslation("users");
  const activity = useQuery(api.admin.users.getActivity, {
    id: userId as any,
  }) as {
    _id: string;
    action: string;
    resource: string;
    resourceId?: string;
    timestamp: number;
  }[] | null | undefined;

  if (activity === undefined) {
    return (
      <div className="space-y-3">
        <div className="h-6 w-48 animate-pulse rounded bg-muted" />
        <div className="h-6 w-64 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (!activity || activity.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Activity className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
        {t("detail.activityEmpty", "Keine Aktivitäten vorhanden.")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activity.map((item) => (
        <div
          key={item._id}
          className="flex items-start gap-3 rounded-lg border p-4"
        >
          <div className="mt-1 h-2 w-2 rounded-full bg-primary" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              {item.action}
            </p>
            <p className="text-xs text-muted-foreground">
              {item.resource} {item.resourceId ? `• ${item.resourceId}` : ""}
            </p>
            <p className="text-xs text-muted-foreground">
              {new Date(item.timestamp).toLocaleString()}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
